import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table, Alert, Badge, Tabs, Tab } from "react-bootstrap";
import { MdAdd, MdDeleteOutline } from "react-icons/md";
import { clienteApi } from "../../services/api/clienteApi";
import { comedorApi } from "../../services/api/comedorApi";
import { tipoDocumentoApi } from "../../services/api/tipoDocumentoApi";
import { tipoOrigenTrabajadorApi } from "../../services/api/tipoOrigenTrabajadorApi";
import { comensalDietaApi } from "../../services/api/comensalDietaApi";
import { tipoDietaApi } from "../../services/api/tipoDietaApi";
import { useComedorComensal } from "../../services/hooks/useComedorComensal";
import { toast } from "react-toastify";

const ModalComensal = ({
  show,
  onHide,
  onSave,
  comensal,
}) => {
  // Estados locales para evitar bucles infinitos
  const [tipoDocumentos, setTipoDocumentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [comedores, setComedores] = useState([]);
  const [tiposOrigenTrabajador, setTiposOrigenTrabajador] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estados para tipo de origen y dieta activa
  const [tipoOrigenTrabajadorId, setTipoOrigenTrabajadorId] = useState("");
  const [dietaActiva, setDietaActiva] = useState(null);
  const [todasLasDietas, setTodasLasDietas] = useState([]);
  const [fechaFinDieta, setFechaFinDieta] = useState("");
  const [loadingDieta, setLoadingDieta] = useState(false);
  
  // Estados para agregar nueva dieta
  const [showAgregarDieta, setShowAgregarDieta] = useState(false);
  const [tiposDieta, setTiposDieta] = useState([]);
  const [loadingTiposDieta, setLoadingTiposDieta] = useState(false);
  const [nuevaDieta, setNuevaDieta] = useState({
    id_tipo_dieta: "",
    fecha_inicio: "",
    fecha_fin: "",
    observaciones: "",
    activo: true
  });
  
  // Estado para el tab activo
  const [activeTab, setActiveTab] = useState("datos");
  
  // Hook para gestionar asignaciones de comedores
  const {
    comedoresAsignados,
    loading: loadingAsignaciones,
    asignarComedor,
    desasignarComedor,
    fetchComedoresAsignados,
    addComedorLocal,
    removeComedorLocal,
    clearComedoresAsignados
  } = useComedorComensal();
  
  // Obtener proyecto de la sesión
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Estados del formulario
  const [nombresCompletos, setNombresCompletos] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [tipoDocumentoComedor, setTipoDocumentoComedor] = useState("");
  const [numeroDocumentoComedor, setNumeroDocumentoComedor] = useState("");
  const [cargo, setCargo] = useState("");
  const [referencia, setReferencia] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [idCliente, setIdCliente] = useState("");
  const [permiteDuplicadosAtenciones, setPermiteDuplicadosAtenciones] = useState(false);
  const [error, setError] = useState("");
  
  // Estados para la nueva área de asignación de comedores
  const [comedorSeleccionado, setComedorSeleccionado] = useState("");
  const [showAsignaciones, setShowAsignaciones] = useState(false);

  // Filtrar comedores por proyecto de la sesión
  const comedoresFiltrados = comedores.filter(comedor => 
    !selectedProject || comedor.id_proyecto === selectedProject.id
  );

  // Los clientes se cargan por usuario, por lo que ya están filtrados implícitamente
  // Solo mostramos los clientes del usuario actual que tiene acceso al proyecto
  const clientesFiltrados = clientes;

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    const loadData = async () => {
      if (show && selectedProject) {
        setLoading(true);
        try {
          // Cargar tipos de documento
          const tiposResponse = await tipoDocumentoApi.getAll();
          setTipoDocumentos(tiposResponse.data || []);
          
          // Cargar todos los clientes
          const clientesResponse = await clienteApi.getAllClientes();
          setClientes(clientesResponse.data.data || clientesResponse.data || []);
          
          // Cargar comedores filtrados por proyecto
          const comedoresResponse = await comedorApi.getByProyecto(selectedProject.id);
          setComedores(comedoresResponse.data || []);
          
          // Cargar tipos de origen de trabajador
          const tiposOrigenResponse = await tipoOrigenTrabajadorApi.getAll();
          setTiposOrigenTrabajador(tiposOrigenResponse || []);
        } catch (error) {
          console.error('Error cargando datos del modal:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadData();
  }, [show, selectedProject?.id]);
  
  // Cargar tipos de dieta
  useEffect(() => {
    const loadTiposDieta = async () => {
      if (show) {
        try {
          setLoadingTiposDieta(true);
          const response = await tipoDietaApi.getAll();
          const tipos = response.data?.data || response.data || [];
          setTiposDieta(tipos.filter(tipo => tipo.activo !== false));
        } catch (error) {
          console.error('Error cargando tipos de dieta:', error);
        } finally {
          setLoadingTiposDieta(false);
        }
      }
    };
    
    loadTiposDieta();
  }, [show]);

  // Cargar todas las dietas cuando se edita un comensal
  useEffect(() => {
    const loadDietas = async () => {
      if (show && comensal?.id) {
        setLoadingDieta(true);
        try {
          // Obtener todas las dietas del comensal
          const response = await comensalDietaApi.getDietasByComensal(comensal.id);
          // Python retorna array directo, PHP retorna {success: true, data: [...]}
          const dietas = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          
          // Guardar todas las dietas
          setTodasLasDietas(dietas);
          
          // Buscar dieta activa (sin fecha_fin o con fecha_fin futura)
          const hoy = new Date().toISOString().split('T')[0];
          const dietaActivaEncontrada = dietas.find(dieta => {
            if (!dieta.activo) return false;
            if (!dieta.fecha_fin) return true; // Sin fecha fin = activa
            return dieta.fecha_fin >= hoy; // Fecha fin futura = activa
          });
          
          setDietaActiva(dietaActivaEncontrada || null);
          if (dietaActivaEncontrada) {
            setFechaFinDieta(dietaActivaEncontrada.fecha_fin || "");
          } else {
            setFechaFinDieta("");
          }
        } catch (error) {
          console.error('Error cargando dietas:', error);
          setDietaActiva(null);
          setTodasLasDietas([]);
          setFechaFinDieta("");
        } finally {
          setLoadingDieta(false);
        }
      } else {
        setDietaActiva(null);
        setTodasLasDietas([]);
        setFechaFinDieta("");
      }
    };
    
    loadDietas();
  }, [show, comensal?.id]);

  useEffect(() => {
    if (comensal) {
      setNombresCompletos(comensal.nombre || "");
      setTipoDocumento(comensal.tipo_documento_identidad || "");
      setNumeroDocumento(comensal.numero_documento_identidad || "");
      setTipoDocumentoComedor(comensal.tipo_documento_comedor || "");
      setNumeroDocumentoComedor(comensal.numero_documento_comedor || "");
      setCargo(comensal.cargo || "");
      setReferencia(comensal.referencia || "");
      setEmpresa(comensal.empresa || "");
      setIdCliente(comensal.id_cliente || "");
      setTipoOrigenTrabajadorId(comensal.tipo_origen_trabajador_id || "");
      setPermiteDuplicadosAtenciones(Boolean(comensal.permite_duplicados_atenciones));
      
      // Cargar comedores asignados si estamos editando
      if (comensal.id) {
        setShowAsignaciones(true);
        fetchComedoresAsignados(comensal.id);
      }
    } else {
      setNombresCompletos("");
      setTipoDocumento("");
      setNumeroDocumento("");
      setTipoDocumentoComedor("");
      setNumeroDocumentoComedor("");
      setCargo("");
      setReferencia("");
      setEmpresa("");
      setIdCliente("");
      setTipoOrigenTrabajadorId("");
      setPermiteDuplicadosAtenciones(false);
      setShowAsignaciones(false);
      setComedorSeleccionado("");
      clearComedoresAsignados();
    }
    setError("");
  }, [comensal, show]);



  const handleSave = async () => {
    if (!nombresCompletos.trim() || !tipoDocumento || !numeroDocumento.trim() || 
        !tipoDocumentoComedor || !numeroDocumentoComedor.trim() || !cargo.trim() ||
        !idCliente) {
      setError("Los campos marcados con * son obligatorios");
      return;
    }

    if (!selectedProject) {
      setError("No hay un proyecto seleccionado en la sesión");
      return;
    }

    const data = {
      nombre: nombresCompletos.trim(),
      tipo_documento_identidad: tipoDocumento,
      numero_documento_identidad: numeroDocumento.trim(),
      tipo_documento_comedor: tipoDocumentoComedor,
      numero_documento_comedor: numeroDocumentoComedor.trim(),
      cargo: cargo.trim(),
      referencia: referencia.trim() || null, // Enviar null si está vacío
      empresa: empresa.trim() || null, // Enviar null si está vacío
      id_cliente: parseInt(idCliente), // Usar id_cliente para la relación con cliente
      tipo_origen_trabajador_id: tipoOrigenTrabajadorId ? parseInt(tipoOrigenTrabajadorId) : null,
      permite_duplicados_atenciones: permiteDuplicadosAtenciones,
      // Los comedores se manejan por separado en la nueva área
      comedores: comedoresAsignados.map(c => c.id)
    };

    // Si hay dieta activa y se especificó fecha de fin, actualizar la dieta
    if (dietaActiva && fechaFinDieta) {
      try {
        await comensalDietaApi.update(dietaActiva.id, {
          fecha_fin: fechaFinDieta
        });
      } catch (error) {
        console.error('Error al actualizar fecha fin de dieta:', error);
        setError("Error al actualizar la fecha de fin de la dieta");
        return;
      }
    }

    onSave(data);
  };
  
  // Función para finalizar dieta activa
  const handleFinalizarDieta = async () => {
    if (!dietaActiva) return;
    
    // Usar la fecha del calendario si está seleccionada, sino usar la fecha de hoy
    const fechaFin = fechaFinDieta || new Date().toISOString().split('T')[0];
    setFechaFinDieta(fechaFin); // Actualizar el campo de fecha
    
    try {
      // Actualizar fecha_fin y desactivar la dieta
      await comensalDietaApi.update(dietaActiva.id, {
        fecha_fin: fechaFin,
        activo: false
      });
      
      // Recargar todas las dietas
      const response = await comensalDietaApi.getDietasByComensal(comensal.id);
      const dietas = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setTodasLasDietas(dietas);
      
      const hoyDate = new Date().toISOString().split('T')[0];
      const dietaActivaEncontrada = dietas.find(dieta => {
        if (!dieta.activo) return false;
        if (!dieta.fecha_fin) return true;
        return dieta.fecha_fin >= hoyDate;
      });
      
      setDietaActiva(dietaActivaEncontrada || null);
      setFechaFinDieta(dietaActivaEncontrada?.fecha_fin || fechaFin);
      
      setError("");
    } catch (error) {
      console.error('Error al finalizar dieta:', error);
      setError("Error al finalizar la dieta");
    }
  };

  // Función para agregar nueva dieta
  const handleAgregarDieta = async () => {
    const comensalId = comensal?.id;
    if (!comensalId || !selectedProject) {
      toast.error("Debe seleccionar un comensal y un proyecto");
      return;
    }

    // Validaciones
    if (!nuevaDieta.id_tipo_dieta) {
      toast.error("Debe seleccionar un tipo de dieta");
      return;
    }

    if (!nuevaDieta.fecha_inicio) {
      toast.error("Debe ingresar una fecha de inicio");
      return;
    }

    if (!nuevaDieta.fecha_fin) {
      toast.error("Debe ingresar una fecha de fin");
      return;
    }

    if (nuevaDieta.fecha_fin < nuevaDieta.fecha_inicio) {
      toast.error("La fecha de fin debe ser mayor o igual a la fecha de inicio");
      return;
    }

    try {
      setLoadingDieta(true);
      
      const data = {
        id_proyecto: selectedProject.id,
        id_comensal: comensalId,
        numero_documento_comedor: comensal.numero_documento_comedor || comensal.numero_documento_identidad || numeroDocumentoComedor || "",
        id_tipo_dieta: parseInt(nuevaDieta.id_tipo_dieta),
        fecha_inicio: nuevaDieta.fecha_inicio,
        fecha_fin: nuevaDieta.fecha_fin,
        observaciones: nuevaDieta.observaciones.trim() || null,
        activo: nuevaDieta.activo
      };

      await comensalDietaApi.create(data);
      
      toast.success("Dieta agregada exitosamente");
      
      // Recargar todas las dietas
      const response = await comensalDietaApi.getDietasByComensal(comensalId);
      const dietas = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setTodasLasDietas(dietas);
      
      const hoy = new Date().toISOString().split('T')[0];
      const dietaActivaEncontrada = dietas.find(dieta => {
        if (!dieta.activo) return false;
        if (!dieta.fecha_fin) return true;
        return dieta.fecha_fin >= hoy;
      });
      setDietaActiva(dietaActivaEncontrada || null);
      
      // Resetear formulario
      setNuevaDieta({
        id_tipo_dieta: "",
        fecha_inicio: "",
        fecha_fin: "",
        observaciones: "",
        activo: true
      });
      setShowAgregarDieta(false);
    } catch (error) {
      console.error('Error al agregar dieta:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || "Error al agregar la dieta";
      toast.error(errorMessage);
    } finally {
      setLoadingDieta(false);
    }
  };

  // Función para agregar un comedor a la lista de asignaciones
  const handleAgregarComedor = async () => {
    if (!comedorSeleccionado) {
      setError("Por favor seleccione un comedor para agregar");
      return;
    }

    // Verificar si el comedor ya está asignado
    const yaAsignado = comedoresAsignados.some(c => c.id === parseInt(comedorSeleccionado));
    if (yaAsignado) {
      setError("Este comedor ya está asignado al comensal");
      return;
    }

    if (comensal && comensal.id) {
      // Si estamos editando un comensal existente, usar la API
      const success = await asignarComedor(comensal.id, parseInt(comedorSeleccionado));
      if (success) {
        setComedorSeleccionado("");
        setError("");
        // El hook useComedorComensal ya actualiza comedoresAsignados automáticamente
      }
    } else {
      // Si estamos creando un comensal nuevo, agregar a la lista local
      const comedorInfo = comedores.find(c => c.id === parseInt(comedorSeleccionado));
      if (comedorInfo) {
        // Usar la función del hook para actualizar el estado local
        addComedorLocal(comedorInfo);
        setComedorSeleccionado("");
        setError("");
        
        // Mostrar mensaje de éxito
        console.log('Comedor agregado localmente:', comedorInfo.nombre);
      }
    }
  };

  // Función para eliminar un comedor de la lista de asignaciones
  const handleEliminarComedor = async (comedorId) => {
    if (comensal && comensal.id) {
      // Si estamos editando un comensal existente, usar la API
      await desasignarComedor(comensal.id, comedorId);
    } else {
      // Si estamos creando un comensal nuevo, remover de la lista local
      removeComedorLocal(comedorId);
      console.log('Comedor eliminado de lista local:', comedorId);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {comensal ? "Editar Comensal" : "Nuevo Comensal"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* TAB 1: DATOS DEL COMENSAL */}
            <Tab eventKey="datos" title="Datos del Comensal">
              <div className="mt-3">
                {/* ÁREA SUPERIOR: Información de Proyecto y Cliente */}
                <div className="bg-light p-3 rounded mb-4">
            <h6 className="mb-3 text-primary">Información del Proyecto y Cliente</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Proyecto</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedProject ? selectedProject.nombre : "No hay proyecto seleccionado"}
                    readOnly
                    className="bg-white"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={idCliente}
                    onChange={(e) => setIdCliente(e.target.value)}
                    required
                    disabled={!!comensal} // Deshabilitar cuando estamos editando
                    style={{
                      backgroundColor: comensal ? '#f8f9fa' : 'white',
                      cursor: comensal ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">Seleccione un cliente...</option>
                    {clientesFiltrados.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre_cliente || cliente.nombre_sociedad || cliente.nombre}
                      </option>
                    ))}
                  </Form.Select>
                  {comensal && (
                    <Form.Text className="text-muted">
                      <small>
                        <i className="fas fa-info-circle me-1"></i>
                        El cliente no se puede cambiar al editar un comensal existente
                      </small>
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* ÁREA INFERIOR: Datos Personales del Comensal */}
          <div className="bg-light p-3 rounded">
            <h6 className="mb-3 text-primary">Datos Personales del Comensal</h6>
            
            {/* Apellidos y Nombres */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Apellidos y Nombres *</Form.Label>
                  <Form.Control
                    type="text"
                    value={nombresCompletos}
                    onChange={(e) => setNombresCompletos(e.target.value)}
                    placeholder="Ingrese apellidos y nombres completos"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Documento de Identidad */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Documento de Identidad *</Form.Label>
                  <Form.Select
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    required
                  >
                    <option value="">Seleccione un tipo...</option>
                    {tipoDocumentos.map((tipo) => (
                      <option key={tipo.id} value={tipo.codigo}>
                        {tipo.codigo}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Documento de Identidad *</Form.Label>
                  <Form.Control
                    type="text"
                    value={numeroDocumento}
                    onChange={(e) => setNumeroDocumento(e.target.value)}
                    placeholder="Ingrese el número de documento"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Documento de Comedor */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Documento de Comedor *</Form.Label>
                  <Form.Select
                    value={tipoDocumentoComedor}
                    onChange={(e) => setTipoDocumentoComedor(e.target.value)}
                    required
                  >
                    <option value="">Seleccione un tipo...</option>
                    {tipoDocumentos.map((tipo) => (
                      <option key={tipo.id} value={tipo.codigo}>
                        {tipo.codigo}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Documento de Comedor *</Form.Label>
                  <Form.Control
                    type="text"
                    value={numeroDocumentoComedor}
                    onChange={(e) => setNumeroDocumentoComedor(e.target.value)}
                    placeholder="Ingrese el número de documento de comedor"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Cargo y Referencia */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cargo *</Form.Label>
                  <Form.Control
                    type="text"
                    value={cargo}
                    onChange={(e) => setCargo(e.target.value)}
                    placeholder="Ej: Gerente, Analista, Operario, etc."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Referencia</Form.Label>
                  <Form.Control
                    type="text"
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    placeholder="Información adicional (opcional)"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Empresa y Tipo de Origen */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Empresa</Form.Label>
                  <Form.Control
                    type="text"
                    value={empresa}
                    onChange={(e) => setEmpresa(e.target.value)}
                    placeholder="Nombre de la empresa (opcional)"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Origen de Trabajador</Form.Label>
                  <Form.Select
                    value={tipoOrigenTrabajadorId}
                    onChange={(e) => setTipoOrigenTrabajadorId(e.target.value)}
                  >
                    <option value="">Seleccione un tipo de origen...</option>
                    {tiposOrigenTrabajador.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.id} - {tipo.nombre}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <small>Seleccione 1 (Local) o 2 (Foráneo)</small>
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="permite-duplicados-atenciones-switch"
                    label="Permite atenciones duplicadas (mismo DNI varias veces en el turno / Excel)"
                    checked={permiteDuplicadosAtenciones}
                    onChange={(e) => setPermiteDuplicadosAtenciones(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Usar solo para supervisores de almuerzo transportado u otros casos autorizados.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
              </div>
            </Tab>

            {/* TAB 2: DIETAS */}
            <Tab eventKey="dietas" title="Dietas">
              <div className="mt-3">
                {/* Área de Dieta Activa */}
                <div className="bg-light p-3 rounded">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 text-primary">Dieta Activa</h6>
              <Button
                variant="success"
                size="sm"
                onClick={() => {
                  setShowAgregarDieta(!showAgregarDieta);
                  if (!showAgregarDieta) {
                    // Resetear formulario al abrir
                    setNuevaDieta({
                      id_tipo_dieta: "",
                      fecha_inicio: "",
                      fecha_fin: "",
                      observaciones: "",
                      activo: true
                    });
                  }
                }}
                disabled={!comensal?.id}
              >
                <MdAdd className="me-1" />
                {showAgregarDieta ? "Cancelar" : "Agregar Dieta"}
              </Button>
            </div>
            
            {showAgregarDieta && (
              <div className="bg-white p-3 rounded mb-3 border">
                <h6 className="mb-3">Nueva Dieta</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Dieta <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={nuevaDieta.id_tipo_dieta}
                        onChange={(e) => setNuevaDieta({...nuevaDieta, id_tipo_dieta: e.target.value})}
                        disabled={loadingTiposDieta}
                      >
                        <option value="">Seleccione un tipo de dieta</option>
                        {tiposDieta.map((tipo) => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha de Inicio <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={nuevaDieta.fecha_inicio}
                        onChange={(e) => setNuevaDieta({...nuevaDieta, fecha_inicio: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha de Fin <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={nuevaDieta.fecha_fin}
                        onChange={(e) => setNuevaDieta({...nuevaDieta, fecha_fin: e.target.value})}
                        min={nuevaDieta.fecha_inicio || undefined}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Observaciones</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={nuevaDieta.observaciones}
                        onChange={(e) => setNuevaDieta({...nuevaDieta, observaciones: e.target.value})}
                        maxLength={1000}
                        placeholder="Observaciones adicionales (opcional)"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Dieta activa"
                        checked={nuevaDieta.activo}
                        onChange={(e) => setNuevaDieta({...nuevaDieta, activo: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAgregarDieta(false);
                      setNuevaDieta({
                        id_tipo_dieta: "",
                        fecha_inicio: "",
                        fecha_fin: "",
                        observaciones: "",
                        activo: true
                      });
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAgregarDieta}
                    disabled={loadingDieta || !nuevaDieta.id_tipo_dieta || !nuevaDieta.fecha_inicio || !nuevaDieta.fecha_fin}
                  >
                    {loadingDieta ? "Guardando..." : "Guardar Dieta"}
                  </Button>
                </div>
              </div>
            )}

            {comensal?.id && (
              <>
                {/* Mensaje destacado si tiene dieta activa */}
                {dietaActiva && (
                  <Alert variant="success" className="mb-3" style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    ✓ Este comensal tiene una dieta activa: <strong>{dietaActiva.tipo_dieta?.nombre || dietaActiva.nombre_tipo_dieta || 'N/A'}</strong>
                  </Alert>
                )}

                {loadingDieta && !showAgregarDieta ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <span className="ms-2">Cargando información de dietas...</span>
                  </div>
                ) : (
                  <>
                    {/* Sección para finalizar dieta activa */}
                    {dietaActiva && (
                      <div className="mb-3">
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Fecha de Fin de Dieta</Form.Label>
                              <Form.Control
                                type="date"
                                value={fechaFinDieta}
                                onChange={(e) => setFechaFinDieta(e.target.value)}
                                min={dietaActiva.fecha_inicio || undefined}
                              />
                              <Form.Text className="text-muted">
                                <small>Establezca una fecha de fin para finalizar la dieta activa</small>
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6} className="d-flex align-items-end">
                            <Button
                              variant="warning"
                              onClick={handleFinalizarDieta}
                              className="w-100"
                            >
                              Finalizar Dieta
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Tabla con todas las dietas */}
                    {todasLasDietas.length > 0 ? (
                      <div className="mt-3">
                        <h6 className="mb-2">Historial de Dietas</h6>
                        <div className="table-responsive">
                          <Table striped bordered hover size="sm">
                            <thead className="table-light">
                              <tr>
                                <th>Tipo de Dieta</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Fin</th>
                                <th>Estado</th>
                                <th>Observaciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {todasLasDietas.map((dieta) => {
                                const hoy = new Date().toISOString().split('T')[0];
                                const esActiva = dieta.activo && (!dieta.fecha_fin || dieta.fecha_fin >= hoy);
                                const esLaActiva = dietaActiva && dieta.id === dietaActiva.id;
                                
                                return (
                                  <tr 
                                    key={dieta.id}
                                    style={esLaActiva ? { backgroundColor: '#d4edda' } : {}}
                                  >
                                    <td>
                                      {dieta.tipo_dieta?.nombre || dieta.nombre_tipo_dieta || 'N/A'}
                                      {esLaActiva && (
                                        <Badge bg="success" className="ms-2">ACTIVA</Badge>
                                      )}
                                    </td>
                                    <td>{dieta.fecha_inicio || 'N/A'}</td>
                                    <td>{dieta.fecha_fin || 'Sin fecha fin'}</td>
                                    <td>
                                      {esActiva ? (
                                        <Badge bg="success">Activa</Badge>
                                      ) : (
                                        <Badge bg="secondary">Inactiva</Badge>
                                      )}
                                    </td>
                                    <td>
                                      <small>{dieta.observaciones || '-'}</small>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    ) : !showAgregarDieta ? (
                      <Alert variant="secondary" className="mb-0">
                        <small>Este comensal no tiene dietas registradas.</small>
                      </Alert>
                    ) : null}
                  </>
                )}
              </>
            )}
                </div>
              </div>
            </Tab>

            {/* TAB 3: ACCESO A COMEDORES */}
            <Tab eventKey="comedores" title="Acceso a Comedores">
              <div className="mt-3">
                {/* ÁREA: Asignación de Comedores */}
                <div className="bg-light p-3 rounded">
            <h6 className="mb-3 text-primary">Asignación de Comedores</h6>
            
            {/* Controles para agregar comedores */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Seleccionar Comedor</Form.Label>
                  <Form.Select
                    value={comedorSeleccionado}
                    onChange={(e) => setComedorSeleccionado(e.target.value)}
                  >
                    <option value="">Seleccione un comedor para agregar...</option>
                    {comedoresFiltrados
                      .filter(comedor => !comedoresAsignados.some(c => c.id === comedor.id))
                      .map((comedor) => (
                        <option key={comedor.id} value={comedor.id}>
                          {comedor.nombre}
                        </option>
                      ))
                    }
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button 
                  variant="success" 
                  onClick={handleAgregarComedor}
                  disabled={!comedorSeleccionado || loadingAsignaciones}
                  className="d-flex align-items-center gap-2"
                >
                  <MdAdd size={20} />
                  Agregar
                </Button>
              </Col>
            </Row>

            {/* Grilla de comedores asignados */}
            <div className="mt-3">
              <h6 className="mb-2">Comedores Asignados</h6>
              {loadingAsignaciones ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <span className="ms-2">Cargando asignaciones...</span>
                </div>
              ) : comedoresAsignados.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Nombre del Comedor</th>
                        <th>Proyecto</th>
                        <th style={{ width: "100px" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comedoresAsignados.map((comedor) => (
                        <tr key={comedor.id}>
                          <td>{comedor.nombre}</td>
                          <td>{selectedProject?.nombre || 'N/A'}</td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleEliminarComedor(comedor.id)}
                              disabled={loadingAsignaciones}
                              title="Eliminar asignación"
                            >
                              <MdDeleteOutline size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  <small>
                    No hay comedores asignados a este comensal.
                    {comensal ? ' Use el selector de arriba para agregar comedores.' : ' Los comedores se podrán asignar después de crear el comensal.'}
                  </small>
                </Alert>
              )}
            </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {comensal ? "Actualizar" : "Crear"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalComensal;
